import { Action, ActionsDropdown, Button, Card, CardBody, CardHeader, Icon, Map, PieChart, Tooltip } from '@aidkitorg/component-library';
import { createStagedComponent, SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';
import { FilterConfig, FilterGroup } from '@aidkitorg/types/lib/admin-panel/filters';
import { RawGeoJSONSource } from '@aidkitorg/types/lib/survey';
import { useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { useDashboards } from '../../../../Contexts/DashboardsContext';
import { useAsyncEffect } from '../../../../Hooks/AsyncEffect';
import { Metric } from './Metric';
import { ComponentEditorModal } from './ComponentEditorModal';
import { ChartDataModal } from './ChartDataModal';
import { useHistory, NavLink } from 'react-router-dom';
import { SummaryTable } from './SummaryTable';
import { Report } from './Report';
import { useLocalizedStrings } from '../../../../Localization';
import { useReports } from '../../../../Contexts/ReportsContext';
import { AdminPanelBarChart } from './AdminPanelBarChart';
import { AdminPanelLineChart } from './AdminPanelLineChart';
import { BooleanExprToFilterGroupConverter } from '../../../shared/utils/filterConverters';
import { renderFilterGroup } from '../../../shared/utils/filterDisplayUtils';
import React from 'react';

/**
 * Base dashboard component that handles all common behaviors of dashboard
 * components and decides which type of component to render.
 */
type DashboardComponentCardProps = {
  id: string
  component: SupportedDashboardComponent,
  isEditMode?: boolean,
  width?: number,
  height?: number,
  viewFilters?: FilterConfig,
  dashboardFilter?: any, // BooleanExpr from dashboard config
}

export const DashboardComponentCard = ({ id, component, isEditMode, width, height, viewFilters, dashboardFilter }: DashboardComponentCardProps) => {
  const L = useLocalizedStrings()
  const [dashboardComponent, setDashboardComponent] = useState<SupportedDashboardComponent>(component)
  const { setStagedComponents, getDashboardComponentTranslation } = useDashboards()
  const { getReportById, fetchReportById } = useReports()
  const [queryResult, setQueryResult] = useState<TranslatedComponentResult | undefined>()
  const [error, setError] = useState<{code: number} | null>(null)
  const [reportFetched, setReportFetched] = useState(false)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isChartDataModalOpen, setIsChartDataModalOpen] = useState(false)

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const [chartHeight, setChartHeight] = useState(300)

  // Get current title and description (for Report components, use live data; for others, use component config)
  const getCurrentTitleAndDescription = useMemo(() => {
    if (dashboardComponent.kind === 'Report' && dashboardComponent.reportId && reportFetched) {
      const currentReport = getReportById(dashboardComponent.reportId);
      if (currentReport) {
        return {
          title: currentReport.title,
          description: currentReport.description
        };
      }
    }
    
    // Fallback to component config for non-Report components or if report not found
    const title = 'title' in dashboardComponent && dashboardComponent.title ? 
      (typeof dashboardComponent.title === 'string' ? dashboardComponent.title : dashboardComponent.title['en']) :
      'Dashboard Component';
    const description = dashboardComponent.description || '';
    
    return { title, description };
  }, [dashboardComponent, reportFetched]);

  useLayoutEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [getCurrentTitleAndDescription.title, getCurrentTitleAndDescription.description, width, height]);

  useEffect(() => {
    setChartHeight(height ? Math.max(height - headerHeight, 120) : 300)
  }, [height, headerHeight])
  
  // This shouldn't happen but in case we render this for a component that isn't a supported kind
  if (!('kind' in component) || !['Hero Number', 'Bar Chart', 'Pie Chart', 'Map', 'Summary Table', 'Report', 'Line Chart'].includes(component.kind)) {
    return (<></>)
  }

  useEffect(() => {
    setDashboardComponent(component);
    setReportFetched(false); // Reset fetch flag when component changes
  }, [component]);

  // Separate effect for fetching report metadata (similar to ReportViewPage)
  useEffect(() => {
    const fetchReport = async () => {
      if (dashboardComponent.kind !== 'Report' || !dashboardComponent.reportId || reportFetched) return;
      
      try {
        await fetchReportById(dashboardComponent.reportId);
        setReportFetched(true);
      } catch (error) {
        console.error('Error fetching report:', error);
      }
    };

    fetchReport();
  }, [dashboardComponent.kind, dashboardComponent, reportFetched]);

  useAsyncEffect(async () => {
    // Report components handle their own data loading, skip translation
    if (dashboardComponent.kind === 'Report') {
      setQueryResult(undefined)
      setError(null)
      return
    }

    const result = await getDashboardComponentTranslation(dashboardComponent, viewFilters, dashboardFilter)
    if (result) {
      if (result.success) {
        setQueryResult(result.data)
        setError(null)
      } else {
        setError({code: result.errorCode || 0})
        setQueryResult(undefined)
      }
    } else {
      console.error('Failed to translate dashboard component:', dashboardComponent)
      setError({code: 0})
      setQueryResult(undefined)
    }
  }, [dashboardComponent, viewFilters, dashboardFilter])

  const EmptyState = () => {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <div className="text-center">
          <div className="text-sm font-medium">{L.admin_panel.reports.no_data_available}</div>
          <div className="text-xs">{L.admin_panel.dashboards.no_data_msg}</div>
        </div>
      </div>
    )
  }

  const ComponentToRender = useMemo(() => {
    switch (dashboardComponent.kind) {
      case 'Hero Number':
        return <Metric component={dashboardComponent} result={queryResult && {
          summaryKey: queryResult.summaryKey,
          data: queryResult.data,
          timing: queryResult.timing,
          rowCount: queryResult.rowCount || 0
        }}/>
      case 'Bar Chart':
        if (!queryResult || !queryResult.data || queryResult.data.length === 0) {
          return <EmptyState />
        }

        return (
          <AdminPanelBarChart
            component={dashboardComponent}
            queryResult={queryResult}
            height={chartHeight}
            tableCaption={getCurrentTitleAndDescription.title}
          />
        )
      case 'Line Chart':
        if (!queryResult || !queryResult.data || queryResult.data.length === 0) {
          return <EmptyState />
        }

        return (
          <AdminPanelLineChart
            component={dashboardComponent}
            queryResult={queryResult}
            height={chartHeight}
            tableCaption={getCurrentTitleAndDescription.title}
          />
        )
      case 'Pie Chart':
        const nameKey = dashboardComponent.groups[0].kind === 'Field' ? dashboardComponent.groups[0].field : ''
        if (queryResult) {
          const dataKey = queryResult.summaryKey
          let processedData = Array.isArray(queryResult.data) ? queryResult.data.map((datum: Record<string, any>, _idx: number) => ({
            ...datum,
            [dataKey]: datum[dataKey] != null ? Number(datum[dataKey]) : null
          })) : []

          // Show empty state if no data
          if (processedData.length === 0) {
            return <EmptyState />
          }

          if (processedData.length > 5) {
            // Sort by data value descending to get largest segments
            processedData.sort((a, b) => (b[dataKey] || 0) - (a[dataKey] || 0))

            // Keep top 4 segments
            const topFour = processedData.slice(0, 4)
            const remaining = processedData.slice(4)

            // Sum up remaining segments
            const otherValue = remaining.reduce((sum, item) => sum + (item[dataKey] || 0), 0)

            // Create "Other" segment
            const otherSegment = {
              [nameKey]: L.admin_panel.dashboards.other,
              [dataKey]: otherValue
            }

            // Combine top 4 with "Other"
            const summarizedData = [...topFour, otherSegment]
            return <PieChart
              height={chartHeight}
              data={summarizedData}
              dataKey={dataKey}
              nameKey={nameKey}
              tableCaption={getCurrentTitleAndDescription.title}
            />
          }

          return <PieChart
            height={chartHeight}
            data={processedData}
            dataKey={dataKey}
            nameKey={nameKey}
            tableCaption={getCurrentTitleAndDescription.title}

          />
        } else {
          return <EmptyState />
        }
      case 'Map':
        // Map components use a different data format (resultsMap)
        if (queryResult && queryResult.data) {
          // Transform the map sources with the GeoJSON data
          const sourcesWithData = dashboardComponent.sources.map((source, index) => {
            if (source.kind !== 'Applicant Data Source') return source

            const layerData = queryResult.data[source.layerName || `layer_${index}`]
            if (!layerData) return source

            return {
              ...source,
              kind: 'Raw GeoJSON Source',
              data: JSON.stringify(layerData.rows),
              // Clear fields that don't apply to Raw GeoJSON sources
              columns: undefined,
              filter: undefined,
              addressField: undefined,
              includeUrl: undefined,
              includeUid: undefined
            } as RawGeoJSONSource
          })

          return (
            <Map
              height={chartHeight}
              mapConfig={{
                ...dashboardComponent,
                sources: sourcesWithData,
                // Clear description field to match formatting
                description: undefined,
              }}
            />
          )
        } else {
          return <></>
        }
      case 'Summary Table':
        return (
          <SummaryTable
            component={dashboardComponent}
            result={queryResult}
            height={chartHeight}
          />
        )
      case 'Report':
        return (
          <Report
            component={dashboardComponent}
            height={chartHeight}
          />
        )
      default:
        return <></>
    }
  }, [dashboardComponent, queryResult, chartHeight, viewFilters]) // Only re-create when actual data changes

  const getReportUrl = () => {
    if (dashboardComponent.kind === 'Report') {
      return `/admin-panel/reports/${dashboardComponent.reportId}`
    }
    return '#'
  }

  // Convert component filter to FilterGroup for badge display
  const filterInfo = useMemo(() => {
    // Check if component supports filters and has a filter applied
    const componentHasFilter =
      (dashboardComponent.kind === 'Hero Number' ||
       dashboardComponent.kind === 'Bar Chart' ||
       dashboardComponent.kind === 'Pie Chart' ||
       dashboardComponent.kind === 'Line Chart'||
       dashboardComponent.kind === 'Map' || 
       dashboardComponent.kind === 'Summary Table') &&
      'filter' in dashboardComponent &&
      dashboardComponent.filter;

    if (componentHasFilter) {
      const filterGroup = BooleanExprToFilterGroupConverter.convert(dashboardComponent.filter);
      if (filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0) {
        // Count total filter conditions (not groups)
        const countConditions = (group: FilterGroup): number => {
          let count = 0;
          group.conditions.forEach((condition) => {
            if ('logic' in condition) {
              count += countConditions(condition as FilterGroup);
            } else {
              count += 1;
            }
          });
          return count;
        };

        const filterCount = countConditions(filterGroup);
        const badges = renderFilterGroup(filterGroup, undefined, false, 'small');

        return { count: filterCount, badges };
      }
    }
    return null;
  }, [dashboardComponent]);

  const titleAndDesc = (
    <div className={isEditMode ? "drag-handle cursor-move" : ""} ref={headerRef}>
      <div className="flex justify-between">
        <div className="flex flex-wrap items-center gap-2 text-sm break-normal px-4 py-2">
          <span>{getCurrentTitleAndDescription.title}</span>
          {filterInfo && filterInfo.count > 0 && (
            <Tooltip
              variant="light"
              text={
                <div className="flex flex-wrap gap-1.5 items-center max-w-sm">
                  {filterInfo.badges}
                </div>
              }
            >
              <span className="text-xs text-blue-600 font-medium cursor-help flex-shrink-0">
                {filterInfo.count} {filterInfo.count === 1 ? L.admin_panel.dashboards.filter_singular : L.admin_panel.dashboards.filter_plural}
              </span>
            </Tooltip>
          )}
        </div>
        {dashboardComponent.kind === 'Report' && !isEditMode && (
          <div>
            <Tooltip text={L.admin_panel.dashboards.open_report} asChild>
              <NavLink
                to={getReportUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8 png-hide"
              >
                <Icon className="text-black" type="solid" name="ArrowTopRightOnSquareIcon" role="decorative" />
              </NavLink>
            </Tooltip>
          </div>
        )}
      </div>
      {getCurrentTitleAndDescription.description && (
        <div className="text-xs break-normal text-gray-700 pb-2 px-4">
          {getCurrentTitleAndDescription.description}
        </div>
      )}
    </div>
  );

  const handleDeleteComponent = () => {
    setStagedComponents((prev) => [...prev].filter((sc) => sc.id !== id))
  }

  const componentActions: Array<Action<SupportedDashboardComponent>> = [];

  const showActionButton = isEditMode || (dashboardComponent.kind === 'Bar Chart' || dashboardComponent.kind === 'Pie Chart' || dashboardComponent.kind === 'Line Chart')

  if (isEditMode) {
    componentActions.push(
      {
        label: L.admin_panel.dashboards.edit,
        onClick: () => {setIsEditModalOpen(true)},
        icon: "PencilIcon",
      },
      {
        label: L.admin_panel.dashboards.duplicate,
        onClick: async () => {
          await duplicateComponent()
        },
        icon: "Square2StackIcon"
      },
      {
        label: L.admin_panel.dashboards.delete,
        onClick: () => {
          handleDeleteComponent()
        },
        icon: "TrashIcon",
        variant: "warn",
      }
    );
  } else if (dashboardComponent.kind === 'Bar Chart' || dashboardComponent.kind === 'Pie Chart' || dashboardComponent.kind === 'Line Chart') {
    componentActions.push({
      label: 'View Data',
      onClick: () => setIsChartDataModalOpen(true),
      icon: 'TableCellsIcon'
    })
  }


  
  const ErrorState = () => {
    const errorMsg = () => {
      // Error codes from CommonErrorCode enum in backend
      switch (error?.code) {
        case 10002: // UNAUTHORIZED
          return 'You do not have sufficient permissions to query this data.'
        case 10005: // DATABASE_ERROR
          return 'There was an issue executing your query. Please check your component configuration and try again.'
        case 10007: // BAD_REQUEST
          return 'There was an issue translating your component to SQL. Please check your component configuration and try again.'
        case 10006: // INTERNAL_ERROR
          return 'An internal server error occurred. Please try again later.'
        case 0:
        default:
          return 'Error rendering dashboard component. Please check your component configuration and try again.'
      }
    }

    return (
      <div className="pt-2 flex items-center flex-col text-gray-600">
        <div><Icon strokeWidth={2} size="sm" role="decorative" name="ExclamationTriangleIcon"/></div>
        <div className="text-xs max-w-96 text-center">{errorMsg()}</div>
      </div>
    )
  }

  const duplicateComponent = async () => {
    setStagedComponents(prev => {
      const index = prev.findIndex(sc => sc.id === id)
      if (index === -1) return prev

      const original = prev[index]
      
      // Handle Report components differently since they don't need translation
      if (original.config.kind === 'Report') {
        const duplicatedComponent = {
          ...original.config,
          title: 'My Report', // Generic title, will be dynamically resolved from report data
        };
        
        const stagedComponent = createStagedComponent(duplicatedComponent)
        
        return [
          ...prev.slice(0, index + 1),
          stagedComponent,
          ...prev.slice(index + 1),
        ]
      }
      
      // For non-Report components, handle as before
      const duplicatedComponent = {
        ...original.config,
        title: original.config.title + ' (Copy)',
      };

      (async () => {
        const translations = await getDashboardComponentTranslation(duplicatedComponent, undefined, dashboardFilter)
        if (translations && translations.success) {
          const stagedComponent = createStagedComponent(duplicatedComponent, translations.data)

          setStagedComponents(current => {
            const insertIndex = current.findIndex(sc => sc.id === id)
            if (insertIndex === -1) return current

            return [
              ...current.slice(0, insertIndex + 1),
              stagedComponent,
              ...current.slice(insertIndex + 1),
            ]
          })
        }
      })()

      return prev
    })
  }

  const cardButton = <ActionsDropdown 
    actions={componentActions}
    item={dashboardComponent}
  />

  return <Card className="rounded-md h-full">
    <CardHeader
      title={titleAndDesc}
      actionButton={showActionButton ? cardButton : undefined}
    />
    <CardBody className="flex-1 flex">
      <div className="flex-1 flex items-center justify-center">
        {error ? <ErrorState /> : ComponentToRender}
        <ComponentEditorModal
          id={id}
          component={dashboardComponent}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          currentTitle={getCurrentTitleAndDescription.title}
        />
        <ChartDataModal
          isOpen={isChartDataModalOpen}
          onClose={() => setIsChartDataModalOpen(false)}
          component={dashboardComponent}
          queryResult={queryResult}
          chartHeight={chartHeight}
        />
      </div>
    </CardBody>
  </Card>
}